import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { Users, Search, Phone, Mail, MapPin, IdCard, Calendar, MessageCircle, Edit2, X, Save, Trash2 } from 'lucide-react';
import CustomAlert from './shared/CustomAlert';

// ⭐ Remarketing -> Contactos (pedido de Angelo, 2026-08-05): esta pantalla
// ahora unifica DOS orígenes en una sola lista, vía GET /api/pos/contacts:
//   - Clientes registrados desde el carrito de Caja/Mesas/Pedidos (pos_customers).
//   - Contactos de WhatsApp que NUNCA compraron (crm_contacts sin pos_customer_id).
// Un contacto que compró Y escribió por WhatsApp aparece UNA sola vez (la
// fila de cliente, con su crm_contact_id enlazado) -- el backend ya resuelve
// esa deduplicación, ver el comentario en GET /api/pos/contacts.
// Edición total: cada contacto se puede editar por completo desde acá.
// Los mismos datos (cédula/sector/teléfono) también se pueden ver y editar
// desde el panel lateral de Conversaciones cuando ese contacto escribe por
// WhatsApp -- mismos endpoints, dos puntos de entrada.

const PAGE_SIZE = 20;

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const EMPTY_FORM = { first_name: '', last_name: '', cedula: '', phone: '', sector: '', email: '' };
const EMPTY_CRM_FORM = { name: '', cedula: '', phone_alt: '', email: '', custom_fields: {} };

export default function Contactos({ token }) {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customAlert, setCustomAlert] = useState(null);

  // ⭐ Edición: solo tiene sentido para contactos que ya son "cliente POS"
  // (pos_customer_id != null) -- un contacto puro de WhatsApp sin compras
  // se edita desde Conversaciones (nombre/email/temperatura vía
  // PATCH /crm/contacts/:id), no tiene los campos de facturación que edita
  // este formulario (cédula, sector).
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // ⭐ Edición total (pedido de Angelo, 2026-08-05: "que desde Contactos
  // también se pueda editar los datos, o eliminarlos"). Este segundo
  // formulario edita el LADO WhatsApp (crm_contacts: nombre, cédula,
  // teléfono alterno, email) vía PATCH /crm/contacts/:id -- separado del
  // formulario de arriba (que edita pos_customers) porque son dos
  // registros distintos con su propio id; un contacto "Cliente + WhatsApp"
  // puede necesitar editar cualquiera de los dos lados.
  const [editingCrmContact, setEditingCrmContact] = useState(null);
  const [crmForm, setCrmForm] = useState(EMPTY_CRM_FORM);
  const [savingCrm, setSavingCrm] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  useEffect(() => {
    axios.get(`${API_URL}/api/pos/crm/custom-fields`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCustomFields(res.data || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 280);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const loadContacts = useCallback(async ({ reset = false } = {}) => {
    setLoading(true);
    try {
      const nextPage = reset ? 1 : page + 1;
      const params = { page: nextPage, limit: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await axios.get(`${API_URL}/api/pos/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      const { rows, page: p, total: t, hasMore: hm } = res.data;
      setContacts(prev => (reset ? rows : [...prev, ...rows]));
      setPage(p);
      setTotal(t);
      setHasMore(hm);
    } catch (err) {
      if (!err.response) console.error('Error al cargar contactos:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, debouncedSearch]);

  useEffect(() => {
    loadContacts({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const openEdit = (c) => {
    setEditingContact(c);
    setEditForm({
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      cedula: c.cedula || '',
      phone: c.phone || '',
      sector: c.sector || '',
      email: c.email || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingContact) return;
    if (!editForm.first_name.trim()) {
      showAlert('warning', 'Nombre requerido', 'El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/pos/customers/${editingContact.pos_customer_id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAlert('success', 'Contacto actualizado', 'Los datos se guardaron correctamente');
      setEditingContact(null);
      loadContacts({ reset: true });
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo guardar el contacto');
    } finally {
      setSaving(false);
    }
  };

  const openEditCrm = (c) => {
    let custom = {};
    try { custom = typeof c.custom_fields === 'string' ? JSON.parse(c.custom_fields || '{}') : (c.custom_fields || {}); } catch (_) {}
    setEditingCrmContact(c);
    setCrmForm({
      name: c.first_name || '',
      cedula: c.cedula || '',
      phone_alt: c.phone_alt || '',
      email: c.email || '',
      custom_fields: custom
    });
  };

  const handleSaveCrmEdit = async () => {
    if (!editingCrmContact) return;
    if (!crmForm.name.trim()) {
      showAlert('warning', 'Nombre requerido', 'El nombre es obligatorio');
      return;
    }
    setSavingCrm(true);
    try {
      await axios.patch(`${API_URL}/api/pos/crm/contacts/${editingCrmContact.crm_contact_id}`, {
        name: crmForm.name.trim(),
        cedula: crmForm.cedula.trim(),
        phone_alt: crmForm.phone_alt.trim(),
        email: crmForm.email.trim(),
        custom_fields: crmForm.custom_fields,
      }, { headers: { Authorization: `Bearer ${token}` } });
      showAlert('success', 'Contacto actualizado', 'Los datos se guardaron correctamente');
      setEditingCrmContact(null);
      loadContacts({ reset: true });
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo guardar el contacto');
    } finally {
      setSavingCrm(false);
    }
  };

  // ⭐ Elimina TODO lo que representa esta fila -- si está fusionada
  // (cliente que también escribió por WhatsApp), borra ambas mitades. Cada
  // endpoint ya desvincula antes de borrar su propia mitad (ver comentarios
  // en el backend), así que no hay riesgo de arrastrar la otra por error.
  const handleDeleteContact = async () => {
    if (!deletingContact) return;
    setSaving(true);
    try {
      const requests = [];
      if (deletingContact.pos_customer_id) requests.push(axios.delete(`${API_URL}/api/pos/customers/${deletingContact.pos_customer_id}`, { headers: { Authorization: `Bearer ${token}` } }));
      if (deletingContact.crm_contact_id) requests.push(axios.delete(`${API_URL}/api/pos/crm/contacts/${deletingContact.crm_contact_id}`, { headers: { Authorization: `Bearer ${token}` } }));
      await Promise.all(requests);
      showAlert('success', 'Contacto eliminado', 'Se eliminó correctamente.');
      setDeletingContact(null);
      loadContacts({ reset: true });
    } catch (error) {
      showAlert('error', 'Error', error.response?.data?.error || 'No se pudo eliminar el contacto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" /> Contactos
          </h2>
          <p className="text-sm text-gray-500">Clientes registrados en Caja/Mesas/Pedidos y contactos de WhatsApp, en un solo lugar.</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 text-center">
          <p className="text-xs text-orange-600 font-medium">Total contactos</p>
          <p className="text-lg font-bold text-orange-700">{total}</p>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por cédula, nombre, teléfono o correo..."
          className="w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {contacts.length === 0 && loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white rounded-lg shadow-sm">
          <Users size={40} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm font-medium">
            {debouncedSearch ? 'No hay contactos que coincidan con la búsqueda.' : 'Todavía no hay contactos registrados.'}
          </p>
          {!debouncedSearch && (
            <p className="text-xs mt-1">Se registran desde la sección "Cliente" del carrito en Caja, Mesas o Pedidos, o al escribirte por WhatsApp.</p>
          )}
        </div>
      ) : (
        <>
          {/* Vista tabla (desktop) */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                  <th className="text-left px-4 py-3 font-semibold">Origen</th>
                  <th className="text-left px-4 py-3 font-semibold">Canal</th>
                  <th className="text-left px-4 py-3 font-semibold">Cédula</th>
                  <th className="text-left px-4 py-3 font-semibold">Teléfono</th>
                  <th className="text-left px-4 py-3 font-semibold">Sector</th>
                  {customFields.length > 0 && <th className="text-left px-4 py-3 font-semibold">Datos extra</th>}
                  <th className="text-left px-4 py-3 font-semibold">Correo</th>
                  <th className="text-left px-4 py-3 font-semibold">Registrado</th>
                  <th className="text-right px-4 py-3 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contacts.map(c => (
                  <tr key={`${c.source}-${c.pos_customer_id || c.crm_contact_id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{c.first_name} {c.last_name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        c.crm_contact_id ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {c.crm_contact_id && <MessageCircle className="w-3 h-3" />}
                        {c.source === 'customer' ? (c.crm_contact_id ? 'Cliente + WhatsApp' : 'Cliente') : 'Solo WhatsApp'}
                      </span>
                    </td>
                    {/* ⭐ Canal de origen (pedido de Angelo, 2026-08-08): desde
                        que un mismo teléfono puede tener un contacto POR
                        CADA canal de WhatsApp que le escribió (ver
                        processCrmMessageQueue en el backend), esta columna
                        es la única forma de distinguir esas filas -- un
                        cliente sin crm_contact_id (solo compró en Caja,
                        nunca escribió) no tiene canal. */}
                    <td className="px-4 py-3 text-gray-600">{c.channel_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.cedula || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.phone || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.sector || '—'}</td>
                    {customFields.length > 0 && <td className="px-4 py-3 text-gray-600"><div className="space-y-0.5">{customFields.slice(0, 3).map(field => { let values = {}; try { values = typeof c.custom_fields === 'string' ? JSON.parse(c.custom_fields || '{}') : (c.custom_fields || {}); } catch (_) {} const value = values[field.field_key]; return value ? <div key={field.field_key} className="text-xs"><span className="text-gray-400">{field.label}:</span> {String(value)}</div> : null; })}</div></td>}
                    <td className="px-4 py-3 text-gray-600">{c.email || '—'}</td>
                    <td className="px-4 py-3 text-gray-400">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {c.pos_customer_id && (
                          <button
                            onClick={() => openEdit(c)}
                            title="Editar ficha de cliente (POS)"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                          >
                            <Edit2 className="w-3.5 h-3.5" /> Editar
                          </button>
                        )}
                        {c.crm_contact_id && (
                          <button
                            onClick={() => openEditCrm(c)}
                            title="Editar datos de WhatsApp (nombre, cédula, teléfono alterno)"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 hover:text-green-800"
                          >
                            <Edit2 className="w-3.5 h-3.5" /> {c.pos_customer_id ? 'Datos WhatsApp' : 'Editar'}
                          </button>
                        )}
                        <button
                          onClick={() => setDeletingContact(c)}
                          title="Eliminar contacto"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vista tarjetas (móvil) */}
          <div className="md:hidden space-y-2">
            {contacts.map(c => (
              <div key={`${c.source}-${c.pos_customer_id || c.crm_contact_id}`} className="bg-white rounded-lg shadow-sm p-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-800">{c.first_name} {c.last_name}</p>
                  <div className="flex items-center gap-2">
                    {c.pos_customer_id && (
                      <button onClick={() => openEdit(c)} className="text-blue-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {c.crm_contact_id && (
                      <button onClick={() => openEditCrm(c)} className="text-green-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => setDeletingContact(c)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                  c.crm_contact_id ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {c.crm_contact_id && <MessageCircle className="w-3 h-3" />}
                  {c.source === 'customer' ? (c.crm_contact_id ? 'Cliente + WhatsApp' : 'Cliente') : 'Solo WhatsApp'}
                </span>
                <div className="mt-1.5 space-y-1 text-xs text-gray-500">
                  {c.channel_name && <p className="flex items-center gap-1.5"><MessageCircle size={12} /> {c.channel_name}</p>}
                  {c.cedula && <p className="flex items-center gap-1.5"><IdCard size={12} /> {c.cedula}</p>}
                  {c.phone && <p className="flex items-center gap-1.5"><Phone size={12} /> {c.phone}</p>}
                  {c.sector && <p className="flex items-center gap-1.5"><MapPin size={12} /> {c.sector}</p>}
                  {c.email && <p className="flex items-center gap-1.5"><Mail size={12} /> {c.email}</p>}
                  <p className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(c.created_at)}</p>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="text-center mt-4">
              <button
                onClick={() => loadContacts({ reset: false })}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 disabled:opacity-50"
              >
                {loading ? 'Cargando...' : `Cargar más (${contacts.length} de ${total})`}
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal de edición total */}
      {editingContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Editar contacto</h3>
              <button onClick={() => setEditingContact(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Apellido</label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cédula</label>
                <input
                  type="text"
                  value={editForm.cedula}
                  onChange={(e) => setEditForm(f => ({ ...f, cedula: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sector</label>
                <input
                  type="text"
                  value={editForm.sector}
                  onChange={(e) => setEditForm(f => ({ ...f, sector: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Correo</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setEditingContact(null)}
                disabled={saving}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de datos de WhatsApp (crm_contacts) */}
      {editingCrmContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Editar datos de WhatsApp</h3>
              <button onClick={() => setEditingCrmContact(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500 -mt-1">
                El número de WhatsApp ({editingCrmContact.phone}) no se puede cambiar acá -- es la identidad real del canal.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={crmForm.name}
                  onChange={(e) => setCrmForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cédula</label>
                <input
                  type="text"
                  value={crmForm.cedula}
                  onChange={(e) => setCrmForm(f => ({ ...f, cedula: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono alterno</label>
                <input
                  type="text"
                  value={crmForm.phone_alt}
                  onChange={(e) => setCrmForm(f => ({ ...f, phone_alt: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Correo</label>
                <input
                  type="email"
                  value={crmForm.email}
                  onChange={(e) => setCrmForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </div>
              {customFields.length > 0 && (
                <div className="border-t pt-3 mt-3 space-y-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Campos personalizados</p>
                  {customFields.map(field => (
                    <div key={field.field_key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
                      {field.type === 'select' ? (
                        <select value={crmForm.custom_fields?.[field.field_key] || ''} onChange={(e) => setCrmForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.field_key]: e.target.value } }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                          <option value="">Sin especificar</option>
                          {(field.options || []).map(option => <option key={option} value={option}>{option}</option>)}
                        </select>
                      ) : (
                        <input type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} value={crmForm.custom_fields?.[field.field_key] || ''} onChange={(e) => setCrmForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.field_key]: e.target.value } }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex gap-3">
              <button
                onClick={() => setEditingCrmContact(null)}
                disabled={savingCrm}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCrmEdit}
                disabled={savingCrm}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {savingCrm ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingContact && (
        <CustomAlert
          type="confirm"
          title="¿Eliminar este contacto?"
          message={`Se eliminará "${deletingContact.first_name} ${deletingContact.last_name || ''}".${deletingContact.crm_contact_id ? ' Esto borra también su historial de conversación de WhatsApp.' : ''} Esta acción no se puede deshacer.`}
          onConfirm={handleDeleteContact}
          onClose={() => setDeletingContact(null)}
          confirmDelaySeconds={deletingContact.crm_contact_id ? 3 : 0}
        />
      )}

      {customAlert && (
        <CustomAlert
          type={customAlert.type}
          title={customAlert.title}
          message={customAlert.message}
          onClose={() => setCustomAlert(null)}
        />
      )}
    </div>
  );
}
